import { createContext, useContext, useMemo } from 'react';

import type {
  UseDeleteInventoryList,
  UseInventoryListFetchById,
  UseInventoryListsFetch,
  UseSubmitInventoryList,
} from './InventoryListHookTypes';
import type {
  UseAccountOptionsFetch,
  UseEditUser,
  UseGetUsersTableData,
  UseUserCreate,
  UseUserDeleteModal,
  UseUserFetch,
  UseUserPageHeaderActions,
  UseUsersListFetch,
} from './UserHookTypes';

export interface AdministrationProviderConfig {
  isOa?: boolean;
  useCreateUser: UseUserCreate;
  useUsersListFetch: UseUsersListFetch;
  useGetUsersTableData: UseGetUsersTableData;
  useAccountOptionsFetch: UseAccountOptionsFetch;
  useInventoryListsFetch: UseInventoryListsFetch;
  useInventoryListByIdFetch: UseInventoryListFetchById;
  useSubmitInventoryList: UseSubmitInventoryList;
  useDeleteInventoryList: UseDeleteInventoryList;
  useUserFetch: UseUserFetch;
  useUserPageHeaderActions: UseUserPageHeaderActions;
  useEditUser: UseEditUser;
  useUserDeleteModal: UseUserDeleteModal;
}

export interface AdministrationProviderProps {
  children: React.ReactNode;
  administrationConfig: AdministrationProviderConfig;
}

export type AdministrationContext = AdministrationProviderConfig;

const administrationContext = createContext<AdministrationContext>({
  isOa: false,
  useAccountOptionsFetch: () => {
    throw new Error('useAccountOptionsFetch: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useCreateUser: () => {
    throw new Error('useCreateUser: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useGetUsersTableData: () => {
    throw new Error('useGetUsersTableData: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useUsersListFetch: () => {
    throw new Error('useUsersListFetch: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useInventoryListsFetch: () => {
    throw new Error('useInventoryListsFetch: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useInventoryListByIdFetch: () => {
    throw new Error('useInventoryListByIdFetch: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useSubmitInventoryList: () => {
    throw new Error('useSubmitInventoryList: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useDeleteInventoryList: () => {
    throw new Error('useDeleteInventoryList: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useUserFetch: () => {
    throw new Error('useUserFetch: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useUserPageHeaderActions: () => {
    throw new Error('useUserPageHeaderActions: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useEditUser: () => {
    throw new Error('useEditUser: Not implemented. Ensure you are inside the AdministrationContext');
  },
  useUserDeleteModal: () => {
    throw new Error('useUserDeleteModal: Not implemented. Ensure you are inside the AdministrationContext');
  },
});

export const useAdministrationContext = () => useContext(administrationContext);

export function AdministrationProvider({ children, administrationConfig }: AdministrationProviderProps) {
  const value: AdministrationContext = useMemo(
    () => ({
      ...administrationConfig,
    }),
    [administrationConfig]
  );

  return <administrationContext.Provider value={value}>{children}</administrationContext.Provider>;
}
